// Every page must RENDER before a print PDF is handed over (2026-10-07).
//
// The structural gate (printPdfCheck.ts) proves each page can be turned into
// PostScript. This one opens the finished file the way a reader does (pdf.js —
// the engine the 3D preview already uses) and draws every page, small: a page
// that throws here would print as an error sheet. Any failure blocks the
// export, with the page numbers.
//
// ⚠️ MEMORY: pdf.js needs its own copy of the file. A 200 MB interior on a
// phone with little memory would crash the tab that is making the book, so the
// check is skipped there (the structural gate still ran) — print PDFs are made
// by the server render and on computers, where it always runs.
import { PrintPdfError, type PrintProblem } from './printPdfCheck';

/** Should this device run the render check? Pure, for the guard test. */
export function canRenderCheck(env: { userAgent?: string; deviceMemoryGb?: number }): boolean {
  const ua = env.userAgent ?? '';
  if (/iPhone|iPad|iPod|Android.+Mobile/i.test(ua)) return false;
  if (typeof env.deviceMemoryGb === 'number' && env.deviceMemoryGb < 4) return false;
  return true;
}

async function loadPdfjs() {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  }
  return pdfjs;
}

/** Draws every page at `longEdgePx`; throws PrintPdfError listing the pages that failed. */
export async function assertPagesRender(bytes: Uint8Array, label: string, longEdgePx = 160): Promise<{ pages: number; skipped: boolean }> {
  const nav = typeof navigator !== 'undefined' ? navigator as Navigator & { deviceMemory?: number } : null;
  if (!canRenderCheck({ userAgent: nav?.userAgent, deviceMemoryGb: nav?.deviceMemory })) {
    console.info(`[render-check] ${label}: skipped on this device (memory) — structural print check already passed`);
    return { pages: 0, skipped: true };
  }
  const pdfjs = await loadPdfjs();
  // A COPY: pdf.js may take ownership of the buffer it is given.
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false }).promise;
  const failed: PrintProblem[] = [];
  try {
    for (let n = 1; n <= doc.numPages; n += 1) {
      try {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: longEdgePx / Math.max(base.width, base.height) });
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));
        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) throw new Error('canvas unavailable');
        await page.render({ canvasContext: ctx, viewport }).promise;
        page.cleanup();
      } catch (e) {
        failed.push({ page: n, what: `does not render (${(e as Error)?.message ?? 'error'})` });
      }
    }
    if (failed.length) throw new PrintPdfError(label, failed);
    return { pages: doc.numPages, skipped: false };
  } finally {
    await doc.destroy();
  }
}
